import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import _ from 'lodash';
import styled from 'styled-components';
import hooks from '@g4/automation-hooks';

import filledStar from '../../images/icon-24-star-yellow.svg';
import halfStar from '../../images/icon-24-half-star-yellow.svg';
import emptyStar from '../../images/icon-24-star-gray.svg';

const Star = styled.img`
  height: ${props => (props.isSunseekerPromotion ? '33px' : '')};
  width: ${props => (props.isSunseekerPromotion ? '32px' : '')};
`;

const VisuallyHidden = styled.div`
  position: absolute !important;
  height: 1px;
  width: 1px;
  overflow: hidden;
  clip: rect(1px, 1px, 1px, 1px);
`;

const InteractiveStarLabelContainer = styled.label``;

const AsciiStar = styled(Star.withComponent('span'))`
  width: auto;
  height: auto;
`;

/** @visibleName Star Rating */
export class StarRating extends PureComponent {
  constructor(props) {
    super(props);

    this.getSelectedStar = this.getSelectedStar.bind(this);
  }

  static propTypes = {
    /** Determines class attribute */
    className: PropTypes.string,
    /** Rating value out of `max` */
    value: PropTypes.number.isRequired,
    /** The maximum possible `value` */
    max: PropTypes.number.isRequired,
    /** Draw stars with ascii characters instead of image assets */
    ascii: PropTypes.bool,
    /** Size of star rating images */
    scale: PropTypes.number,
    /** If the component is not readOnly it can be clicked and it will return the clicked value */
    readOnly: PropTypes.bool,
    /** When a star is clicked the onSelection returns the value to the parent component */
    onSelection: PropTypes.func,
    /** Provide a name when using multiple interactive StarRating components for the html 'name' attribute on input nodes */
    name: PropTypes.string,
    /** Associates a specific action onto an element for the benefit of automated clients consuming it */
    automationHook: PropTypes.string,
    isSunseekerPromotion: PropTypes.bool,
  };

  static defaultProps = {
    ascii: false,
    scale: 1,
    readOnly: true,
    onSelection: Function.prototype,
    name: 'rating',
  };

  getSelectedStar(i) {
    if (!this.props.readOnly) {
      // The indexing starts from 0 while we count from 1
      // Toggle value if selecting the same value as the currently selected one
      const nextSelection = i + 1 === this.props.value ? 0 : i + 1;

      this.props.onSelection(nextSelection);
    }
  }

  render() {
    const {
      ascii,
      value,
      max,
      scale,
      readOnly,
      name,
      automationHook,
      isSunseekerPromotion,
    } = this.props;

    const getStar = (type, index, isSunseekerPromotion) => {
      const stars = {
        ascii: {
          filled: <AsciiStar>&#9733;</AsciiStar>,
          halfFilled: <AsciiStar>&#43;</AsciiStar>,
          empty: <AsciiStar>&#9734;</AsciiStar>,
        },
        default: {
          filled: (
            <Star
              key={index}
              src={filledStar}
              scale={scale}
              alt=""
              isSunseekerPromotion={isSunseekerPromotion}
            />
          ),
          halfFilled: (
            <Star
              key={index}
              src={halfStar}
              scale={scale}
              alt=""
              isSunseekerPromotion={isSunseekerPromotion}
            />
          ),
          empty: (
            <Star
              key={index}
              src={emptyStar}
              scale={scale}
              alt=""
              isSunseekerPromotion={isSunseekerPromotion}
            />
          ),
        },
      };

      return ascii ? stars.ascii[type] : stars.default[type];
    };

    const isInteger = x => {
      return x % 1 === 0;
    };

    const staticStars = [...new Array(max)].map((_, i) => {
      if (i + 1 <= value) {
        return getStar('filled', i, isSunseekerPromotion);
      }

      if (!isInteger(value) && i + 1 === Math.ceil(value)) {
        return getStar('halfFilled', i, isSunseekerPromotion);
      }

      return getStar('empty', i, isSunseekerPromotion);
    });

    const interactiveStars = _.times(max, i => (
      <InteractiveStarLabelContainer key={i}>
        <VisuallyHidden>
          <input
            onClick={() => this.getSelectedStar(i)}
            type="radio"
            name={name}
          />
          <span>{i + 1} Stars</span>
        </VisuallyHidden>

        {i < value
          ? getStar('filled', i, isSunseekerPromotion)
          : getStar('empty', i, isSunseekerPromotion)}
      </InteractiveStarLabelContainer>
    ));

    return (
      <div
        className={this.props.className}
        aria-label={`${value} out of ${max}`}
        {...hooks.hook(automationHook)}
      >
        {readOnly ? staticStars : interactiveStars}
      </div>
    );
  }
}

export default styled(StarRating)`
  display: flex;
`;
